import {
  OperationSummary,
  useLeads,
  useLeadSearchOperations,
} from "@inrange/building-manager-api-client";
import { useMemo } from "react";
import { Alert, Badge, Button, Container, Table } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

// Mirrors `summariseSearchTerms` in LeadSearchPage. Plain-English
// summary the operator can scan to recognise their search at a
// glance without opening a JSON blob.
const summariseCriteria = (criteria: Record<string, unknown> | null): string => {
  if (!criteria) return "(unknown criteria)";
  const parts: string[] = ["warehouses"];
  const minArea = criteria.minFloorAreaSqm;
  if (typeof minArea === "number" && minArea > 0) {
    parts.push(`≥ ${Number(minArea).toLocaleString()} m²`);
  }
  const maxArea = criteria.maxFloorAreaSqm;
  if (typeof maxArea === "number" && maxArea > 0) {
    parts.push(`≤ ${Number(maxArea).toLocaleString()} m²`);
  }
  const postcodeOutward = criteria.postcodeOutward;
  if (typeof postcodeOutward === "string" && postcodeOutward.trim()) {
    parts.push(`in ${postcodeOutward.toUpperCase()}`);
    const radius = criteria.radiusKm;
    if (typeof radius === "number" && radius > 0) {
      parts.push(`+ ${radius} km`);
    } else {
      parts.push("only");
    }
  } else {
    parts.push("nationally");
  }
  return parts.join(" ");
};

const formatRelativeTime = (epochSeconds: number | null): string => {
  if (!epochSeconds) return "—";
  const delta = Math.floor(Date.now() / 1000) - epochSeconds;
  if (delta < 60) return `${delta}s ago`;
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
};

const formatElapsed = (op: OperationSummary): string => {
  if (!op.createdAt) return "—";
  const end = op.updatedAt ?? Math.floor(Date.now() / 1000);
  const seconds = end - op.createdAt;
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

const StatusBadge: React.FC<{ status: OperationSummary["status"] }> = ({
  status,
}) => {
  const variant: Record<OperationSummary["status"], string> = {
    pending: "secondary",
    running: "primary",
    completed: "success",
    cancelled: "warning",
    failed: "danger",
  };
  return <Badge bg={variant[status] ?? "secondary"}>{status}</Badge>;
};

const _TerminalBar: React.FC<{
  label: string;
  tone: "completed" | "cancelled" | "failed";
}> = ({ label, tone }) => {
  const colour =
    tone === "completed"
      ? "#198754"
      : tone === "cancelled"
        ? "#ffc107"
        : "#dc3545";
  return (
    <div style={{ minWidth: 180 }}>
      <div
        style={{
          height: 4,
          background: "#dee2e6",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div style={{ height: "100%", width: "100%", background: colour }} />
      </div>
      <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{label}</div>
    </div>
  );
};

const ProgressBar: React.FC<{ op: OperationSummary }> = ({ op }) => {
  // Three cases:
  // 1. Terminal status (completed / cancelled / failed): the bar is
  //    100% with a clear label, regardless of whatever the last
  //    progress write said. Otherwise we'd show stale mid-flight
  //    text like "Finalising" on a row that's already done.
  // 2. Chunked + running: rows-completed estimate from
  //    chunksCompleted * chunkSize.
  // 3. Sync + running: progress.current / progress.total (the
  //    long-running Lambda's 4-stage counter).
  if (op.status === "completed") {
    return <_TerminalBar label="Completed" tone="completed" />;
  }
  if (op.status === "cancelled") {
    return <_TerminalBar label="Cancelled" tone="cancelled" />;
  }
  if (op.status === "failed") {
    return <_TerminalBar label="Failed" tone="failed" />;
  }
  let percent = 0;
  let label = "";
  if (op.chunksTotal > 0) {
    const rowsDone = Math.min(
      op.chunksCompleted * op.chunkSize,
      op.rowsTotal
    );
    percent = op.rowsTotal > 0 ? (rowsDone / op.rowsTotal) * 100 : 0;
    label = `${rowsDone.toLocaleString()} / ${op.rowsTotal.toLocaleString()} rows (batch ${op.chunksCompleted} of ${op.chunksTotal})`;
  } else if (op.progress) {
    // Same math as the in-flight banner on /leads/search to keep
    // the two surfaces consistent. `current` is 0-indexed stage,
    // `total` is stage count; render the user-facing step number
    // as `current + 1` (clamped to total for the brief
    // "Finalising" tick), and weight the bar by adding the
    // within-stage fraction on top of the outer-stage progress.
    const stageNum = Math.min(op.progress.current + 1, op.progress.total);
    const stageFrac =
      op.progress.totalInStage && op.progress.totalInStage > 0 && op.progress.processed != null
        ? op.progress.processed / op.progress.totalInStage
        : 0;
    const overall =
      op.progress.total > 0
        ? (op.progress.current + stageFrac) / op.progress.total
        : 0;
    percent = Math.min(100, overall * 100);
    label = `Step ${stageNum} of ${op.progress.total}: ${op.progress.message}`;
    if (
      op.progress.totalInStage != null &&
      op.progress.totalInStage > 0 &&
      op.progress.processed != null
    ) {
      label += ` (${op.progress.processed}/${op.progress.totalInStage})`;
    }
  }
  return (
    <div style={{ minWidth: 180 }}>
      <div
        style={{
          height: 4,
          background: "#dee2e6",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.min(100, percent)}%`,
            // pending / running rows always blue - terminal states
            // are handled by `_TerminalBar` above, so the only
            // statuses that reach here are pending and running.
            background: "#0d6efd",
            transition: "width 200ms ease",
          }}
        />
      </div>
      <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{label}</div>
    </div>
  );
};

const LeadSearchStatusPage: React.FC = () => {
  document.title = "Search status";
  const navigate = useNavigate();
  const { cancelOperation, deleteOperation } = useLeads();

  // Poll while there are in-flight rows; back off to no-poll when
  // everything is in a terminal state (the user can refresh manually
  // or come back later).
  const { data, isLoading, error, refetch } = useLeadSearchOperations({
    refetchInterval: 5000,
  });
  const operations = useMemo(() => data?.operations ?? [], [data]);
  const inFlightCount = operations.filter(
    (op) => op.status === "pending" || op.status === "running"
  ).length;

  const handleCancel = async (operationKey: string) => {
    if (!window.confirm("Cancel this search? Already-completed batches stay; remaining batches won't run.")) {
      return;
    }
    await cancelOperation.mutateAsync(operationKey);
    refetch();
  };

  const handleDelete = async (operationKey: string) => {
    if (
      !window.confirm(
        "Delete this row from the status page? The underlying enriched data on each VOA row stays in the database; this just clears the operation record."
      )
    ) {
      return;
    }
    await deleteOperation.mutateAsync(operationKey);
    refetch();
  };

  const navigateToSearch = (criteria: Record<string, unknown> | null) => {
    if (!criteria) return;
    const params = new URLSearchParams();
    if (typeof criteria.postcodeOutward === "string" && criteria.postcodeOutward) {
      params.set("postcodeOutward", criteria.postcodeOutward);
    }
    if (typeof criteria.minFloorAreaSqm === "number") {
      params.set("minFloorAreaSqm", String(criteria.minFloorAreaSqm));
    }
    if (typeof criteria.maxFloorAreaSqm === "number") {
      params.set("maxFloorAreaSqm", String(criteria.maxFloorAreaSqm));
    }
    if (typeof criteria.radiusKm === "number") {
      params.set("radiusKm", String(criteria.radiusKm));
    }
    navigate(`/leads/search?${params.toString()}`);
  };

  return (
    <Container fluid style={{ padding: "16px 24px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <h4 style={{ margin: 0 }}>
          Search status
          <Link
            to="/leads/search"
            style={{ marginLeft: 12, fontSize: 13, fontWeight: 400 }}
          >
            back to /leads/search
          </Link>
        </h4>
        <div style={{ fontSize: 12, color: "#666" }}>
          {inFlightCount > 0
            ? `${inFlightCount} search${inFlightCount === 1 ? "" : "es"} in progress · auto-refreshing every 5s`
            : "No searches in progress"}
        </div>
      </div>

      <div
        style={{
          fontSize: 12,
          color: "#666",
          marginBottom: 12,
          padding: "8px 10px",
          background: "#f8f9fa",
          borderRadius: 4,
          border: "1px solid #e9ecef",
        }}
      >
        Lists searches that triggered backend enrichment. Three
        reasons a search you ran might not be visible here:
        <ul style={{ marginBottom: 4, marginTop: 4 }}>
          <li>
            <strong>Fully-cached searches don&rsquo;t appear at all</strong>
            {" "}- they return instantly without queueing any work.
          </li>
          <li>
            Smaller searches (matched &le; 2,500 rows) finish in
            under 15 minutes and{" "}
            <strong>stay visible here for ~24 hours</strong> after
            they were kicked off.
          </li>
          <li>
            Larger searches (matched &gt; 2,500 rows) run in batches,{" "}
            <strong>stay visible for ~30 days</strong>, and are the
            only ones with Cancel + partial-result viewing.
          </li>
        </ul>
      </div>

      {error != null && (
        <Alert variant="danger">Failed to load search status.</Alert>
      )}

      {isLoading && operations.length === 0 ? (
        <div style={{ color: "#999", padding: 20 }}>Loading...</div>
      ) : operations.length === 0 ? (
        <Alert variant="secondary">
          No searches in the last 30 days. Kick off a search from{" "}
          <Link to="/leads/search">/leads/search</Link>.
        </Alert>
      ) : (
        <Table size="sm" hover style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Status</th>
              <th>Criteria</th>
              <th style={{ textAlign: "right" }}>Results</th>
              <th>Kicked off by</th>
              <th>Started</th>
              <th>Elapsed</th>
              <th style={{ width: 220 }}>Progress</th>
              <th style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {operations.map((op) => {
              const isInFlight =
                op.status === "pending" || op.status === "running";
              const hasPartialResults = op.chunksCompleted > 0;
              // Cancel is wired through Step Functions
              // `StopExecution`. Sync ops run inside a single
              // 15-min Lambda with no stop surface, so the button
              // is only meaningful for chunked operations.
              const isCancellable =
                isInFlight && op.operationType === "lead_search_chunked";
              return (
                <tr key={op.operationKey}>
                  <td>
                    <StatusBadge status={op.status} />
                    <Badge
                      bg={
                        op.operationType === "lead_search_chunked"
                          ? "info"
                          : "secondary"
                      }
                      style={{
                        marginLeft: 4,
                        fontSize: 10,
                        fontWeight: 400,
                      }}
                      title={
                        op.operationType === "lead_search_chunked"
                          ? "Runs in batches, supports Cancel + partial results, visible here for ~30 days."
                          : "Runs inline (single Lambda), no Cancel surface, visible here for ~24 hours."
                      }
                    >
                      {op.operationType === "lead_search_chunked"
                        ? "chunked search"
                        : "sync search"}
                    </Badge>
                  </td>
                  <td>
                    <div>{summariseCriteria(op.criteria)}</div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: 10,
                        color: "#999",
                      }}
                    >
                      {op.operationKey.slice(0, 12)}…
                    </div>
                  </td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {op.rowsMatched > 0 ? (
                      op.rowsMatched.toLocaleString()
                    ) : (
                      <span style={{ color: "#aaa" }}>—</span>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: 12 }}>
                      {op.kickedOffBy ?? "—"}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 12 }}>
                      {formatRelativeTime(op.createdAt)}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 12 }}>{formatElapsed(op)}</span>
                  </td>
                  <td>
                    <ProgressBar op={op} />
                    {op.failedRowCount > 0 && (
                      <div
                        style={{ fontSize: 11, color: "#dc3545", marginTop: 2 }}
                      >
                        {op.failedRowCount} row
                        {op.failedRowCount === 1 ? "" : "s"} failed
                      </div>
                    )}
                    {op.error && (
                      <div
                        style={{ fontSize: 11, color: "#dc3545", marginTop: 2 }}
                      >
                        {op.error}
                      </div>
                    )}
                  </td>
                  <td>
                    {(op.status === "completed" || hasPartialResults) && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        style={{ marginRight: 4 }}
                        onClick={() => navigateToSearch(op.criteria)}
                      >
                        {op.status === "completed"
                          ? "Open results"
                          : "View partial results"}
                      </Button>
                    )}
                    {isCancellable && (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        style={{ marginRight: 4 }}
                        onClick={() => handleCancel(op.operationKey)}
                        disabled={cancelOperation.isLoading}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDelete(op.operationKey)}
                      disabled={deleteOperation.isLoading}
                      title="Remove this row from the status page. Doesn't affect the enriched data on the underlying VOA rows."
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default LeadSearchStatusPage;
